<template>
  <button
    v-if="bookVariation"
    v-on:click="buttonClickBook"
    :disabled="disabled"
    class="btn flip-book-add-to-cart-btn"
    type="button"
  >
    {{ buttonText }}
  </button>
</template>

<script>
import productMixin from './mixins/productMixins'
import onlineMixin from './mixins/onlineMixin'
import cartButtonMixin from './mixins/cartButtonMixin'
import { cloneDeep, flatten, forEach, some } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'FlipBookAddToCartBtnBook',
  mixins: [productMixin, onlineMixin, cartButtonMixin],
  props: {
    productId: {
      type: Number,
      default: null
    },
    showImage: {
      type: Boolean,
      default: true
    },
    showName: {
      type: Boolean,
      default: true
    },
    showContent: {
      type: Boolean,
      default: true
    },
    showQuickView: {
      type: Boolean,
      default: false
    },
    showAddToCart: {
      type: Boolean,
      default: true
    },
    isListing: {
      type: Boolean,
      default: false
    },
    isVariationGroupMaster: {
      type: Boolean,
      default: false
    },
    recommendationType: {
      type: String,
      default: null
    },
    recommendationId: {
      type: String,
      default: null
    }
  },
  data () {
    return {
      version: '0.0.1',
      progress: false,
      productCodeId: null,
      bookVariationId: 1,
      eBookVariationId: 2,
      showDeviceSelectionPopup: false
    }
  },
  mounted () {
    this.setProductCodeIdForOneOnOne()
  },
  watch: {
    productId () {
      this.setProductCodeIdForOneOnOne()
    }
  },
  computed: {
    ...mapGetters([
      'cartProductCodeQuantity',
      'productCardModal',
      'getRelatedVariationData',
      'getProductById'
    ]),
    ...mapGetters('cartProductCustomizations', ['getSchemaById']),
    unavailable () {
      return (Number(this.productCodeData.productStock) + Number(this.productCodeData.productPendingStock)) <= 0 || this.bookProductData?.productFinalPrice <= 0
    },
    cartLimitReached () {
      return (Number(this.productCodeData.productStock) + Number(this.productCodeData.productPendingStock)) === Number(this.cartQuantity)
    },
    setDisabled () {
      const firstEditionDate = this.productMetaData?.firstEditionDate ? new Date(this.productMetaData.firstEditionDate) : null
      if (firstEditionDate && firstEditionDate > new Date()) {
        return false
      }
      if (this.isListing && this.isVariationGroupMaster) {
        return false
      }
      return this.stock <= 0 || !this.onlineStatus
    },
    quantity () {
      return this.cartProductCodeQuantity(this.bookProductData?.productCodes[0].productCodeId)
    },
    cartQuantity () {
      return this.cartProductCodeQuantity(this.productCodeId, 0)
    },
    hasAttributes () {
      return some(this.bookProductData?.productCodes, (e) => {
        return e.productCodeAttributes.length > 0
      })
    },
    hasVariations () {
      return this.bookProductData?.variations && Object.keys(this.bookProductData?.variations).length > 0
    },
    hasCustomization () {
      return ('customizationSchemaId' in this.bookProductData) &&
        this.bookProductData?.customizationSchemaId !== null &&
        this.getSchemaById(this.bookProductData?.customizationSchemaId).is_active
    },
    quickViewButtonVisible () {
      return this.productCardModal.open
    },
    stock () {
      return this.bookProductData?.productStock
    },
    buttonBuyText () {
      if (this.stock <= 0) {
        return this.t('product_status.unavailable.coming_soon')
      }

      return this.t('add_to_cart')
    },
    getAttributeIdsWithStock () {
      return flatten(this.bookProductData?.productCodes
        .filter(item => item.productStock > 0)
        .map(item => item.productCodeAttributes))
    },
    bookProductData () {
      if (!this.bookVariation) return null
      const productId = Number(this.bookVariation.product_id) || Number(this.bookVariation.productId)
      return this.getProductById(productId)
    },
    currentProductRelatedVariationEntries () {
      const productRelatedVariationEntries = cloneDeep(this.getRelatedVariationData)

      const entries = []
      productRelatedVariationEntries.map(item => {
        forEach(Object.values(item.variations), (variation) => {
          const object = {
            productId: Number(item.product_id),
            variationId: variation.variation_value_id,
            variationGroupId: variation.variation_group_id,
            stock: item.availabilityStock
          }
          entries.push(object)
          return object
        })
      })
      return entries
    },
    bookVariation () {
      const bookFromVariations = this.currentProductRelatedVariationEntries.find(variation => Number(variation.variationId) === this.bookVariationId)
      const bookProduct = this.isEbook ? null : this.productData
      if (!bookFromVariations && bookProduct) return bookProduct
      if (!bookFromVariations && !bookProduct) return null
      const bookProductId = bookFromVariations.productId
      return this.getRelatedVariationData.find(product => Number(product.product_id) === bookProductId)
    },
    isEbook () {
      return this.productData.is_digital === '1'
    },
    productMetaData () {
      return this.productData?.metaData
    }
  },
  methods: {
    ...mapActions([
      'addToCartClicked'
    ]),
    setProductCodeIdForOneOnOne () {
      if (!this.hasAttributes) {
        this.codeIdChanged(this.bookProductData?.productCodes[0].productCodeId)
      }
    },
    codeIdChanged (productCodeId) {
      this.productCodeId = productCodeId
    },
    buttonClickBook () {
      if (this.stock <= 0 && (!this.isListing && !this.isVariationGroupMaster)) {
        this.btnBuyClickedNoStock()
      } else if (this.isListing && this.isVariationGroupMaster) {
        this.btnBuyClickedForVariationMaster()
      } else {
        this.btnBuyClickedBook()
      }
    },
    btnBuyClickedBook () {
      this.progress = true
      this.addToCartClicked({
        productCodeId: this.bookProductData?.productCodes[0].productCodeId,
        quantity: this.quantity,
        recommendationId: this.recommendationId,
        recommendationType: this.recommendationType
      })
        .then(() => {
          this.progress = false
          this.$emit('modal-close')
        })
    },
    btnBuyClickedNoStock () {
      this.$emit('modal-close')
      // for clients that need click event without stock on button
    },
    /**
     * This is a special case for variation group master products.
     * If the product is a variation group master,
     * and we are in a listing page, we need to redirect to the product's page.
     */
    btnBuyClickedForVariationMaster () {
      // old comment below fwiw:
      // for variations: if isListing && isVariationGroupMaster emits btn-click-no-stock so here we are
      if (this.isListing && this.isVariationGroupMaster) {
        window.location.href = this.bookProductData?.productUrl
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.flip-book-add-to-cart-btn {
  background: rgb(141, 113, 151) url(../img/cart_icon_white.svg) no-repeat 10px 10px;
  border-radius: 3px;
  display: block;
  padding: 10px 10px 10px 50px;
  color: #ffffff;
  text-decoration: none;
  font-weight: 700;
  font-size: 17px;
  border: unset;
}
</style>
